import axios from "axios";
import { Quill } from "react-quill";
import baseURL from "../../Services/Url";

const Font = Quill.import("formats/font");
Font.whitelist = ["arial", "georgia", "times-new-roman", "courier-new"];
Quill.register(Font, true);

const mojibakeRun =
  /[\u00c2\u00c3\u00e2][\u0080-\u00ff\u0152\u0153\u0160\u0161\u0178\u017d\u017e\u0192\u02c6\u02dc\u2013\u2014\u2018\u2019\u201a\u201c\u201d\u201e\u2020\u2021\u2022\u2026\u2030\u2039\u203a\u20ac\u2122]*/g;

const windows1252Bytes = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84,
  0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88,
  0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c,
  0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93,
  0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b,
  0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};

const decodeMojibakeRun = (value) => {
  const bytes = Array.from(value).map((character) => {
    const codePoint = character.charCodeAt(0);
    return windows1252Bytes[codePoint] ?? codePoint;
  });
  if (bytes.some((byte) => byte > 255)) return value;

  const decoded = new TextDecoder("utf-8").decode(new Uint8Array(bytes));
  return decoded.includes("\uFFFD") ? value : decoded;
};

export const normalizePastedText = (value = "") => {
  let normalized = value;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const candidate = normalized.replace(mojibakeRun, decodeMojibakeRun);
    if (candidate === normalized) break;
    normalized = candidate;
  }
  return normalized.replace(/\uFEFF/g, "");
};

const toolbarContainer = [
  [{ font: Font.whitelist }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  ["link", "image"],
  ["clean"],
];

export const quillFormats = [
  "font",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "align",
  "link",
  "image",
];

export const createQuillModules = (imageHandler) => ({
  clipboard: {
    matchers: [
      [3, (node, delta) => {
        delta.ops = (delta.ops || []).map((operation) =>
          typeof operation.insert === "string"
            ? { ...operation, insert: normalizePastedText(operation.insert) }
            : operation
        );
        return delta;
      }],
    ],
  },
  toolbar: {
    container: toolbarContainer,
    handlers: imageHandler ? { image: imageHandler } : undefined,
  },
});

export const uploadQuillImage = (quill, options = {}) => {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");
  input.click();

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", options.folder || "richtext");
    formData.append("entityType", options.entityType || "richtext");

    try {
      const res = await axios.post(`${baseURL}/media/upload`, formData);
      const imageUrl = res.data?.media?.url || res.data?.upload?.Location;

      if (!imageUrl) {
        throw new Error("Image upload did not return a URL");
      }

      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, "image", imageUrl, "user");
      quill.setSelection(range.index + 1, 0);
    } catch (error) {
      if (options.onError) {
        options.onError(error);
      }
    }
  };
};
